import { createAsyncThunk } from "@reduxjs/toolkit"

import { getApiErrorMessage, handleApiError } from "../../../../Utils/thunkHelpers"
import {
  fetchActivationPrefill,
  fetchCodes,
  fetchDescriptors,
  fetchPrograms,
  fetchStatusLogs,
  postActivation,
  postDescriptorStatus,
  postMatchType
} from "../api/services"
import { RECEIPT_BUSINESS_SLICE_NAME as SLICE_NAME } from "./business.constants"

// §5. Two cards (cc + oi). Static metadata + per-BA status rollup.
export const getPrograms = createAsyncThunk(
  `${SLICE_NAME}/get_programs`,
  async (businessAccountId, { dispatch, rejectWithValue }) => {
    const response = await fetchPrograms(businessAccountId)
    if (!response.ok) {
      await handleApiError(dispatch, response)
      return rejectWithValue(await getApiErrorMessage(response))
    }
    return response.data
  }
)

// §6. Descriptor list per program - paginated, 20/page fixed.
export const getDescriptors = createAsyncThunk(
  `${SLICE_NAME}/get_descriptors`,
  async (params, { dispatch, rejectWithValue }) => {
    const response = await fetchDescriptors(params)
    if (!response.ok) {
      await handleApiError(dispatch, response)
      return rejectWithValue(await getApiErrorMessage(response))
    }
    return { program: params.program, page: params.page || 1, data: response.data }
  }
)

// Unfiltered fetch for the activation modal — the page-level list may be
// scoped by search/status, but the modal needs to show every existing
// descriptor as a locked chip regardless of the current filters.
export const getActivationDescriptors = createAsyncThunk(
  `${SLICE_NAME}/get_activation_descriptors`,
  async ({ program, businessAccountId }, { dispatch, rejectWithValue }) => {
    const response = await fetchDescriptors({ program, businessAccountId })
    if (!response.ok) {
      await handleApiError(dispatch, response)
      return rejectWithValue(await getApiErrorMessage(response))
    }
    return response.data
  }
)

// §8.0 Code prefill for the activation modal. Separate from the descriptor
// fetch because it is Order Insight only and the two answer different
// questions — one fills locked chips, this one fills the code fields.
export const getActivationPrefill = createAsyncThunk(
  `${SLICE_NAME}/get_activation_prefill`,
  async ({ businessAccountId }, { dispatch, rejectWithValue }) => {
    const response = await fetchActivationPrefill(businessAccountId)
    if (!response.ok) {
      await handleApiError(dispatch, response)
      return rejectWithValue(await getApiErrorMessage(response))
    }
    return response.data
  }
)

// §8. Add descriptors (+ codes for OI). Refetches the affected program's card
// and list (§14) so the rollup status and any newly-created rows land without a
// second manual refresh.
export const activate = createAsyncThunk(
  `${SLICE_NAME}/activate`,
  async ({ businessAccountId, ...payload }, { dispatch, rejectWithValue }) => {
    const response = await postActivation(payload, businessAccountId)
    if (!response.ok) {
      await handleApiError(dispatch, response)
      return rejectWithValue(await getApiErrorMessage(response))
    }
    dispatch(getPrograms(businessAccountId))
    dispatch(getDescriptors({ program: payload.program, businessAccountId }))
    return response.data
  }
)

// §9. Newest-first history for one record. Keyed by record_id in the slice so
// the popup does not stomp on another row's data if the merchant opens two.
export const getStatusLogs = createAsyncThunk(
  `${SLICE_NAME}/get_status_logs`,
  async ({ recordId, businessAccountId }, { dispatch, rejectWithValue }) => {
    const response = await fetchStatusLogs(recordId, businessAccountId)
    if (!response.ok) {
      await handleApiError(dispatch, response)
      return rejectWithValue({ recordId, message: await getApiErrorMessage(response) })
    }
    return { recordId, data: response.data }
  }
)

// BLOCK 3.5. Match type is a property of the descriptor, so the reducer applies
// the result to every row sharing this record's `billing_descriptor_id` — a
// descriptor enrolled in both programs shows one value, not two.
//
// No-op on re-selecting the current value is the caller's job (the control does
// not fire), per "nothing is sent and nothing is logged".
export const setMatchType = createAsyncThunk(
  `${SLICE_NAME}/set_match_type`,
  async ({ recordId, matchType, businessAccountId }, { dispatch, rejectWithValue }) => {
    const response = await postMatchType({ recordId, matchType, businessAccountId })
    if (!response.ok) {
      await handleApiError(dispatch, response)
      return rejectWithValue(await getApiErrorMessage(response))
    }
    // { descriptor_id, match_type } — keyed by descriptor, not by record.
    return response.data
  }
)

// BLOCK 3.4. The response echoes the stored row, so the reducer takes the
// server's version rather than the optimistic one: applying a manual status can
// move the row somewhere the client did not ask for (CHA1-1374).
//
// `match_type` changes with the status but is not in the response — it is only
// serialized while the descriptor has an Active record, and it is the
// descriptor's own value, which the client has never seen while it is null. So
// the list the change was made from is refetched (`list` carries its
// getDescriptors arg) rather than patched locally (CHA1-2614).
export const setDescriptorStatus = createAsyncThunk(
  `${SLICE_NAME}/set_descriptor_status`,
  async ({ recordId, status, list }, { dispatch, rejectWithValue }) => {
    const response = await postDescriptorStatus({ recordId, status })
    if (!response.ok) {
      await handleApiError(dispatch, response)
      return rejectWithValue(await getApiErrorMessage(response))
    }
    // Awaited: the row's spinner runs until `fulfilled`, and stopping it before
    // the refetched match type is on screen would just move the flicker.
    if (list) await dispatch(getDescriptors(list))
    return { recordId, record: response.data?.record }
  }
)

// BLOCK 5. One call answers two questions: the codes for the requested view, and
// `available_code_types` — which views exist for this BA at all. The latter is
// why this runs even while the descriptor list is showing: without it the toggle
// link cannot know which view comes next, or whether to render at all.
export const getCodes = createAsyncThunk(
  `${SLICE_NAME}/get_codes`,
  async ({ codeType, search, businessAccountId }, { dispatch, rejectWithValue }) => {
    const response = await fetchCodes({ codeType, search, businessAccountId })
    if (!response.ok) {
      await handleApiError(dispatch, response)
      return rejectWithValue(await getApiErrorMessage(response))
    }
    return response.data
  }
)
